<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  pricing: { type: Object, required: true },
  column: { type: String, required: true },
})

const { t } = useI18n()

const display = computed(() => {
  switch (props.column) {
    case 'input_range':
      return props.pricing.tiers.map((tier, index) => tokenRange(props.pricing.tiers, index))
    case 'input_price':
      return props.pricing.tiers.map(tier => money(tier.input_micros_per_m))
    case 'output_price':
      return props.pricing.tiers.map(tier => money(tier.output_micros_per_m))
    case 'cached_price':
      return props.pricing.tiers.map(tier => money(tier.cached_input_micros_per_m))
    case 'cache_write_price':
      return props.pricing.tiers.map(tier => money(tier.cache_write_input_micros_per_m))
    case 'image_price':
      return [money(props.pricing.price_micros_per_image)]
    case 'video_with_input_price':
      return [money(props.pricing.with_video_input_micros_per_m)]
    case 'video_without_input_price':
      return [money(props.pricing.without_video_input_micros_per_m)]
    case 'tts_price':
      return [money(props.pricing.price_micros_per_10k)]
    default:
      throw new Error(`Unhandled model pricing column: ${props.column}`)
  }
})

function tokenRange(tiers, index) {
  const upper = tiers[index].input_up_to
  if (tiers.length === 1) return t('visualGameAdmin.llm.capabilities.pricing.ranges.all')
  if (index === 0) {
    return t('visualGameAdmin.llm.capabilities.pricing.ranges.upTo', { value: tokenCount(upper) })
  }
  const lower = tokenCount(tiers[index - 1].input_up_to)
  if (upper === null) {
    return t('visualGameAdmin.llm.capabilities.pricing.ranges.above', { value: lower })
  }
  return t('visualGameAdmin.llm.capabilities.pricing.ranges.between', {
    from: lower,
    to: tokenCount(upper),
  })
}

function tokenCount(value) {
  if (value % 1_000_000 === 0) return `${value / 1_000_000}M`
  if (value % 1_000 === 0) return `${value / 1_000}K`
  return String(value)
}

function money(micros) {
  return `¥${yuan(micros)}`
}

function yuan(micros) {
  return String(Number(micros) / 1_000_000)
}
</script>

<template>
  <div class="model-pricing">
    <span v-for="(value, index) in display" :key="index">{{ value }}</span>
  </div>
</template>

<style scoped lang="scss">
.model-pricing {
  display: grid;
  gap: var(--drama-space-sm);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
  font-variant-numeric: tabular-nums;

  span {
    line-height: var(--drama-line-height-tight);
  }
}
</style>
